import { useState } from 'react';

import {
  DEFAULT_COST_BASED_PAGES_PER_CREDIT,
  DEFAULT_SEAT_CREDIT_LIMIT,
  LEGACY_PAGES_PER_CREDIT,
} from '../../../../../../../backend/src/billing/billing-constants';
import { CreditCalculationModel } from '../../../../../../../backend/src/billing/billing-types';
import { resolveBillingPlan } from '../../../../../../../backend/src/billing/resolve-billing-plan';
import type { BillingPlanHistoryResponseDto } from '../../../../../../../backend/src/organisation/dto/billing-plan-history.response.dto';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { Icon } from '../../../../components/icon';
import { useCreateBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { dateToDatetimeLocal } from '@/helpers/formatDate';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { cn } from '@/new/lib/utils';

const utcDateTime = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' });

/** `datetime-local` values are in the admin's browser time zone; show the UTC instant they resolve to. */
const toUtcLabel = (value: string) => {
  const time = new Date(value);
  return Number.isNaN(time.getTime()) ? null : `${utcDateTime.format(time)} UTC`;
};

/**
 * Always rendered, with a placeholder until a date is set, so the dialog doesn't shift as dates are entered.
 * `invisible` keeps its space so side-by-side inputs stay aligned.
 */
function UtcHint({ id, label, invisible = false }: { id: string; label: string | null; invisible?: boolean }) {
  return (
    <p
      id={id}
      aria-hidden={label === null || invisible}
      className={cn('text-[13px] text-gray-400', invisible && 'invisible')}
    >
      {label ?? '-- UTC'}
    </p>
  );
}

export function NewBillingPeriodDialog({
  organisationId,
  plans,
  initialPlan,
  currentPlanId,
  onOpenChange,
}: {
  organisationId: string;
  plans: BillingPlanHistoryResponseDto[];
  initialPlan: BillingPlanHistoryResponseDto | null;
  currentPlanId?: string;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutate, isLoading } = useCreateBillingPeriod(organisationId);
  const [endCurrentPeriod, setEndCurrentPeriod] = useState(false);
  const replacing = endCurrentPeriod && currentPlanId !== undefined;
  const [pricing, setPricing] = useState(initialPlan?.pricingModel ?? PricingModel.Usage);
  const [disableKimBilling, setDisableKimBilling] = useState(
    initialPlan?.disableBillingForKimThreads ?? pricing === PricingModel.SeatBased,
  );
  const [model, setModel] = useState(initialPlan?.creditCalculationModel ?? CreditCalculationModel.CostBased);
  const [pages, setPages] = useState(String(initialPlan?.pagesPerCredit ?? DEFAULT_COST_BASED_PAGES_PER_CREDIT));
  const [credits, setCredits] = useState(String(initialPlan?.maxCredits ?? ''));
  const [seats, setSeats] = useState(String(initialPlan?.seatCount ?? ''));
  const [limit, setLimit] = useState(String(initialPlan?.defaultCreditLimit ?? DEFAULT_SEAT_CREDIT_LIMIT));
  const latestEnd = plans.reduce(
    (latest, plan) => Math.max(latest, plan.billingEndDate ? new Date(plan.billingEndDate).getTime() : 0),
    0,
  );
  const [start, setStart] = useState(latestEnd > Date.now() ? dateToDatetimeLocal(new Date(latestEnd)) : '');
  const [end, setEnd] = useState('');
  const startUtc = toUtcLabel(start);
  const endUtc = toUtcLabel(end);
  const [reviewing, setReviewing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  function validateForm(): string | null {
    const startTime = replacing ? Date.now() : new Date(start).getTime();
    const endTime = new Date(end).getTime();
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return 'Enter a start and end date.';
    if (endTime <= startTime) return 'End must be after start.';
    if (endTime <= Date.now()) return 'The period must end in the future.';
    const now = new Date();
    const establishedPlan = resolveBillingPlan(
      plans.filter(
        (plan) =>
          new Date(plan.effectiveFrom) <= now &&
          (plan.billingStartDate === null || new Date(plan.billingStartDate) <= now),
      ),
      now,
    ).plan;
    if (startTime > now.getTime() && pricing !== (establishedPlan?.pricingModel ?? PricingModel.Usage)) {
      return 'A pricing-model switch must start immediately, not in the future.';
    }
    if (
      plans.some(
        (plan) =>
          plan.billingStartDate !== null && new Date(plan.billingStartDate) > now && plan.pricingModel !== pricing,
      )
    ) {
      return 'This pricing model conflicts with an already scheduled billing period.';
    }

    if (
      plans.some(
        (plan) =>
          !(
            replacing &&
            initialPlan &&
            new Date(plan.billingStartDate!).getTime() === new Date(initialPlan.billingStartDate!).getTime() &&
            new Date(plan.billingEndDate!).getTime() === new Date(initialPlan.billingEndDate!).getTime()
          ) &&
          plan.billingStartDate &&
          plan.billingEndDate &&
          new Date(plan.billingStartDate).getTime() < endTime &&
          new Date(plan.billingEndDate).getTime() > startTime,
      )
    ) {
      return 'These dates overlap an existing billing period.';
    }
    const validNumber = (value: string, min: number) =>
      value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= 2147483647;
    if (!validNumber(pages, 1)) return 'Pages per credit must be a whole number between 1 and 2,147,483,647.';
    if (pricing === PricingModel.Usage && !validNumber(credits, 0))
      return 'Maximum credits must be a whole number between 0 and 2,147,483,647.';
    if (pricing === PricingModel.SeatBased) {
      if (!validNumber(seats, 1)) return 'Number of seats must be a whole number between 1 and 2,147,483,647.';
      if (!validNumber(limit, 1)) return 'Default credit limit must be a whole number between 1 and 2,147,483,647.';
    }
    return null;
  }
  const switched = (initialPlan?.pricingModel ?? PricingModel.Usage) !== pricing;
  const fieldClass = 'min-w-0 space-y-2';
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!isLoading) onOpenChange(open);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{reviewing ? 'Review new billing period' : 'Create a new billing period'}</DialogTitle>
          <DialogDescription>
            {replacing
              ? 'The current period ends when you confirm. Choose the new end date and limits for the replacement period.'
              : 'Choose the dates, models and initial limits. The new plan takes effect on its start date.'}
          </DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onChange={() => setValidationError(null)}
          onSubmit={(event) => {
            event.preventDefault();
            if (isLoading) return;
            const error = validateForm();
            setValidationError(error);
            if (error) return;
            if (!reviewing) {
              setReviewing(true);
              return;
            }
            mutate(
              {
                ...(replacing ? { replaceCurrentPlanId: currentPlanId } : {}),
                billingStartDate: replacing ? new Date().toISOString() : new Date(start).toISOString(),
                billingEndDate: new Date(end).toISOString(),
                pricingModel: pricing,
                creditCalculationModel: model,
                pagesPerCredit: Number(pages),
                disableBillingForKimThreads: disableKimBilling,
                ...(pricing === PricingModel.Usage
                  ? { maxCredits: Number(credits) }
                  : { seatCount: Number(seats), defaultCreditLimit: Number(limit) }),
              },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
          className="space-y-6"
        >
          <fieldset disabled={reviewing || isLoading} className="space-y-6 disabled:opacity-80">
            {currentPlanId && (
              <div className="mb-8 flex items-start gap-4">
                <Checkbox
                  id="end-current-period"
                  aria-describedby="end-current-period-description"
                  checked={replacing}
                  disabled={reviewing || isLoading}
                  onCheckedChange={(checked) => {
                    setEndCurrentPeriod(checked === true);
                    setValidationError(null);
                  }}
                  className="mt-0.75"
                />
                <div className="space-y-1">
                  <Label htmlFor="end-current-period">End current period and start a new one immediately</Label>
                  <p id="end-current-period-description" className="text-[13px] text-gray-500">
                    Use this for an early renewal or to change contract terms now. The new period starts with a fresh
                    allowance; unused credits won't carry over.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className={fieldClass}>
                <Label htmlFor="new-period-start">Start date</Label>
                <UtcHint id="new-period-start-utc" label={startUtc} invisible={replacing} />
                {replacing ? (
                  <Input id="new-period-start" value="Immediately on confirmation" disabled />
                ) : (
                  <Input
                    id="new-period-start"
                    type="datetime-local"
                    required
                    value={start}
                    aria-describedby={startUtc ? 'new-period-start-utc' : undefined}
                    onChange={(event) => setStart(event.target.value)}
                  />
                )}
              </div>
              <div className={fieldClass}>
                <Label htmlFor="new-period-end">End date</Label>
                <UtcHint id="new-period-end-utc" label={endUtc} />
                <Input
                  id="new-period-end"
                  type="datetime-local"
                  required
                  value={end}
                  aria-describedby={endUtc ? 'new-period-end-utc' : undefined}
                  onChange={(event) => setEnd(event.target.value)}
                />
              </div>
            </div>
            <div className={fieldClass}>
              <Label htmlFor="new-period-pricing">Pricing model</Label>
              <Select
                value={pricing}
                onValueChange={(value: PricingModel) => {
                  setPricing(value);
                  setDisableKimBilling(value === PricingModel.SeatBased);
                  setValidationError(null);
                }}
                disabled={reviewing || isLoading}
              >
                <SelectTrigger id="new-period-pricing" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PricingModel.Usage}>Usage-based</SelectItem>
                  <SelectItem value={PricingModel.SeatBased}>Seat-based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-5 p-3">
              <Switch
                id="new-period-disable-kim-billing"
                checked={disableKimBilling}
                onCheckedChange={setDisableKimBilling}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label htmlFor="new-period-disable-kim-billing" className="mb-1 font-medium text-gray-700">
                  Disable billing for Kim threads
                </Label>
                <p className="text-gray-500">
                  Kim threads won’t count towards billed credits. Usually on for seat-based organisations.
                </p>
              </div>
            </div>
            <div className={fieldClass}>
              <Label htmlFor="new-period-model">Credit calculation model</Label>
              <Select
                value={model}
                onValueChange={(value: CreditCalculationModel) => {
                  setValidationError(null);
                  setModel(value);
                  setPages(
                    String(
                      value === CreditCalculationModel.Legacy
                        ? LEGACY_PAGES_PER_CREDIT
                        : DEFAULT_COST_BASED_PAGES_PER_CREDIT,
                    ),
                  );
                }}
                disabled={reviewing || isLoading}
              >
                <SelectTrigger id="new-period-model" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CreditCalculationModel.Legacy}>Legacy</SelectItem>
                  <SelectItem value={CreditCalculationModel.CostBased}>Cost-based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className={`${fieldClass} ${pricing === PricingModel.SeatBased ? 'sm:col-span-2' : ''}`}>
                <Label htmlFor="new-period-pages">Pages per credit</Label>
                <Input
                  id="new-period-pages"
                  type="number"
                  required
                  min={1}
                  max={2147483647}
                  step={1}
                  value={pages}
                  onChange={(event) => setPages(event.target.value)}
                  disabled={model === CreditCalculationModel.Legacy}
                />
              </div>
              {pricing === PricingModel.Usage ? (
                <div className={fieldClass}>
                  <Label htmlFor="new-period-credits">Maximum credits</Label>
                  <Input
                    id="new-period-credits"
                    type="number"
                    required
                    min={0}
                    max={2147483647}
                    step={1}
                    value={credits}
                    onChange={(event) => setCredits(event.target.value)}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
                  <div className={fieldClass}>
                    <Label htmlFor="new-period-seats">Number of seats</Label>
                    <Input
                      id="new-period-seats"
                      type="number"
                      required
                      min={1}
                      max={2147483647}
                      step={1}
                      value={seats}
                      onChange={(event) => setSeats(event.target.value)}
                    />
                  </div>
                  <div className={fieldClass}>
                    <Label htmlFor="new-period-limit">Default credit limit</Label>
                    <Input
                      id="new-period-limit"
                      type="number"
                      required
                      min={1}
                      max={2147483647}
                      step={1}
                      value={limit}
                      onChange={(event) => setLimit(event.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </fieldset>
          {reviewing && (
            <div className="space-y-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
              <p>
                Pricing and credit calculation models are fixed for this period. Limits, pages per credit and Kim thread
                billing can be updated while it is current.
              </p>
              {disableKimBilling && <p>Kim threads won’t count towards billed credits in this period.</p>}
              {replacing && (
                <p>
                  The current period ends immediately, including its remaining allowance. A new billing period and
                  allowance start now; unused credits are not carried over. Roles and blocking settings change only if
                  the pricing model changes.
                </p>
              )}
              {switched && (
                <p>
                  {pricing === PricingModel.SeatBased
                    ? 'When this period starts, all users will move to the Seat-based role, receive the default credit limit, and require self-approval for runs. Blocking will be enabled.'
                    : 'When this period starts, all users will move to the Member role, per-user credit limits will close, and blocked users will be unblocked. Blocking will be disabled and the run approval limit will reset to 100,000 pages.'}
                </p>
              )}
              {!switched &&
                pricing === PricingModel.SeatBased &&
                limit !== String(initialPlan?.defaultCreditLimit ?? '') && (
                  <p>
                    {!replacing && new Date(start).getTime() > Date.now()
                      ? 'Existing users keep their individual credit limits. The new default applies to users added once this period starts.'
                      : 'The new default credit limit will replace existing users’ individual credit limits.'}
                  </p>
                )}
            </div>
          )}
          {validationError && (
            <p role="alert" className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-500">
              <Icon className="lucide--alert-circle" /> {validationError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={isLoading}
              onClick={() => (reviewing ? setReviewing(false) : onOpenChange(false))}
            >
              {reviewing ? 'Back' : 'Cancel'}
            </Button>
            <Button type="submit" variant="default" disabled={isLoading}>
              {isLoading
                ? 'Creating…'
                : reviewing
                  ? replacing
                    ? 'End period and create new one'
                    : 'Create billing period'
                  : 'Review billing period'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
